import { type BillingPrefs, defaultBillingPrefs, pickShape } from '@/store/slices/userPreferences';
import i18n from '@/utils/i18n';

import { CRYPTO_NETWORKS } from '../settings/components/BillingSection/cryptoRules';

/**
 * Derives the wallet's payout rails from the owner's saved billing preferences.
 *
 * The owner configures payout methods in Settings → Billing, persisted to user
 * preferences as a `BillingPrefs` object (`bankEnabled` / `cryptoEnabled` toggles
 * plus the per-rail details). The Wallet payout card reads that object to decide
 * what it can offer:
 *   - no configured method  → PAYOUT is disabled, owner is sent to Settings
 *   - exactly one           → PAYOUT pays that rail directly
 *   - both                  → PAYOUT opens a chooser so the owner picks one
 *
 * A rail counts as "configured" only when it is enabled AND its essential
 * destination detail is filled (an account identifier / a wallet address). A
 * half-filled rail is treated as missing, so the owner is steered to complete it.
 */

/** A payout rail the owner can withdraw to (a configured bank account or crypto wallet). */
export type PayoutMethodKind = 'bank' | 'crypto';

/** A presentable summary of one configured payout method (for the footer + chooser). */
export type PayoutMethodSummary = {
  kind: PayoutMethodKind;
  /** Short rail name, e.g. "Bank transfer" / "Crypto wallet". */
  title: string;
  /** Human destination, e.g. "Barclays ···4242" / "USDT on Ethereum ···a1b2". */
  detail: string;
  /** BootstrapIcon name for the rail. */
  icon: string;
};

/** Last four meaningful characters of an identifier, ignoring spaces. */
const last4 = (value: string): string => {
  const compact = (value ?? '').replace(/\s+/g, '');
  return compact.length <= 4 ? compact : compact.slice(-4);
};

/** Tail-mask an identifier as "···1234" (middle-dot ellipsis, never an em dash). */
const tail = (value: string): string => {
  const end = last4(value);
  return end ? `···${end}` : '';
};

/** The primary account identifier across corridors (IBAN / local account / CLABE). */
const bankIdentifier = (b: BillingPrefs): string => (b.iban || b.accountNumber || b.clabe || '').trim();

/** The chosen network's display label (falls back to the raw chain id). */
const networkLabel = (network: string): string =>
  CRYPTO_NETWORKS.find((n) => n.value === network)?.label ?? network;

/** A bank rail is payable when it is enabled and has an account identifier. */
export const hasBankPayout = (b: BillingPrefs | null): boolean =>
  !!b && b.bankEnabled && bankIdentifier(b).length > 0;

/** A crypto rail is payable when it is enabled and has a wallet address. */
export const hasCryptoPayout = (b: BillingPrefs | null): boolean =>
  !!b && b.cryptoEnabled && (b.walletAddress ?? '').trim().length > 0;

export const bankPayoutSummary = (b: BillingPrefs): PayoutMethodSummary => {
  const name = (b.bankName ?? '').trim() || i18n.t('Bank account');
  const masked = tail(bankIdentifier(b));
  return {
    kind: 'bank',
    title: i18n.t('Bank transfer'),
    detail: masked ? `${name} ${masked}` : name,
    icon: 'bi-bank',
  };
};

export const cryptoPayoutSummary = (b: BillingPrefs): PayoutMethodSummary => {
  const asset = (b.cryptoAsset ?? '').trim() || i18n.t('Crypto');
  const network = (b.cryptoNetwork ?? '').trim();
  const label = (b.cryptoLabel ?? '').trim();
  const masked = tail(b.walletAddress);
  const base = network ? i18n.t('{{asset}} on {{network}}', { asset, network: networkLabel(network) }) : asset;
  // A nickname is the clearest label; otherwise asset/network plus the masked tail.
  const detail = label ? `${label} (${base})` : masked ? `${base} ${masked}` : base;
  return {
    kind: 'crypto',
    title: i18n.t('Crypto wallet'),
    detail,
    icon: 'bi-currency-bitcoin',
  };
};

/**
 * Resolve the active payout destination: the first of the given preferences that
 * matches a configured rail wins (e.g. a fresh in-session choice, then the saved
 * preference), falling back to the first configured rail. Returns null when no
 * rail is configured.
 */
export const resolveActivePayout = (
  methods: PayoutMethodSummary[],
  ...preferences: (PayoutMethodKind | '' | null | undefined)[]
): PayoutMethodSummary | null => {
  for (const kind of preferences) {
    const match = kind ? methods.find((m) => m.kind === kind) : undefined;
    if (match) return match;
  }
  return methods[0] ?? null;
};

/** Every configured, payable method from billing prefs (bank first, then crypto). */
export const availablePayoutMethods = (raw: BillingPrefs | null): PayoutMethodSummary[] => {
  if (!raw) return [];
  // The selector hands back a raw cast of whatever the backend stored, which may be
  // a partial or older record; normalise to the current shape so every field is a
  // present string before the summaries read it.
  const b = pickShape(raw, defaultBillingPrefs());
  const methods: PayoutMethodSummary[] = [];
  if (hasBankPayout(b)) methods.push(bankPayoutSummary(b));
  if (hasCryptoPayout(b)) methods.push(cryptoPayoutSummary(b));
  return methods;
};
